import React from 'react';
import { Card, CardActionArea, CardContent, Typography, Box, Chip } from '@mui/material';
import {
  TableRestaurant as TableIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import type { MesaResponse } from '../types/pos.types';
import { ESTADOS_MESA } from '../types/pos.types';
import { formatCurrency } from '../utils/posCalculations';

interface TableCardProps {
  mesa: MesaResponse;
  onClick: (mesa: MesaResponse) => void;
}

const estadoColors: Record<string, string> = {
  [ESTADOS_MESA.DISPONIBLE]: '#4caf50',
  [ESTADOS_MESA.OCUPADA]: '#f44336',
  [ESTADOS_MESA.RESERVADA]: '#ff9800',
  [ESTADOS_MESA.POR_COBRAR]: '#2196f3',
  [ESTADOS_MESA.CERRADA]: '#9e9e9e',
};

export const TableCard: React.FC<TableCardProps> = ({ mesa, onClick }) => {
  const color = estadoColors[mesa.estado] || '#9e9e9e';
  const isOcupada = mesa.estado === ESTADOS_MESA.OCUPADA || mesa.estado === ESTADOS_MESA.POR_COBRAR;

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: color,
        borderWidth: 2,
        backgroundColor: isOcupada ? `${color}10` : undefined,
      }}
    >
      <CardActionArea onClick={() => onClick(mesa)}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TableIcon sx={{ color, fontSize: 20 }} />
              <Typography variant="subtitle2" fontWeight="bold">
                Mesa {mesa.numero}
              </Typography>
            </Box>
            <Chip
              label={mesa.estado}
              size="small"
              sx={{
                bgcolor: color,
                color: 'white',
                height: 20,
                fontSize: '0.65rem',
              }}
            />
          </Box>

          {mesa.nombre && (
            <Typography variant="caption" color="text.secondary" display="block">
              {mesa.nombre}
            </Typography>
          )}

          {mesa.mozoNombre && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {mesa.mozoNombre}
              </Typography>
            </Box>
          )}

          {isOcupada && mesa.totalOrdenActiva != null && (
            <Typography variant="body2" fontWeight="bold" color="primary" sx={{ mt: 0.5 }}>
              {formatCurrency(mesa.totalOrdenActiva)}
            </Typography>
          )}

          {mesa.horaApertura && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
              <TimeIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
              <Typography variant="caption" color="text.disabled">
                {new Date(mesa.horaApertura).toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })}
              </Typography>
            </Box>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default TableCard;
